/* REAL MOMENTS: every clip in the row plays muted while it is on screen and
   stops when it leaves; a tap opens the same clip over the whole screen with
   sound and controls, and closing it stops it. Nothing starts by itself for a
   visitor who asked for less motion. One binding per section. The photo row
   ("Wherever the day goes") shares the rail, the arrows and the shuffle. */
(() => {
  const calm = window.matchMedia('(prefers-reduced-motion: reduce)');

  const shuffle = (list) => {
    for (let i = list.length - 1; i > 0; i -= 1) {
      const j = Math.floor(Math.random() * (i + 1));
      [list[i], list[j]] = [list[j], list[i]];
    }
    return list;
  };

  /* A new row on every visit, dealt before anything in it has loaded.
     A photo row: the product's own photographs first, in a new order, then up
     to "per" of each other product, the products in a new order and taken in
     turn, so two photographs of one pack never stand side by side; whatever is
     not dealt stays hidden and is never fetched. A row of clips: a new order. */
  const deal = (root) => {
    const rail = root.querySelector('.sa-real__rail');
    if (!rail) return;
    const items = [...rail.children];
    if (root.hasAttribute('data-sa-life-deal')) {
      const most = parseInt(root.dataset.most, 10) || items.length;
      const per = parseInt(root.dataset.per, 10) || 3;
      const own = shuffle(items.filter((li) => li.hasAttribute('data-sa-own')));
      const groups = new Map();
      items.filter((li) => !li.hasAttribute('data-sa-own')).forEach((li) => {
        const from = li.dataset.saFrom || '';
        if (!groups.has(from)) groups.set(from, []);
        groups.get(from).push(li);
      });
      const hands = shuffle([...groups.values()].map((g) => shuffle(g).slice(0, per)));
      const others = [];
      for (let r = 0; r < per; r += 1) hands.forEach((h) => { if (h[r]) others.push(h[r]); });
      const row = own.concat(others).slice(0, most);
      const keep = new Set(row);
      items.forEach((li) => { li.hidden = !keep.has(li); });
      rail.append(...row);
    } else if (root.hasAttribute('data-sa-real-deal')) {
      rail.append(...shuffle(items));
    }
  };

  const bind = (root) => {
    if (!root || root.dataset.saRealBound) return;
    root.dataset.saRealBound = '1';
    deal(root);
    const previews = [...root.querySelectorAll('[data-sa-real-preview]')];
    const dialog = root.querySelector('[data-sa-real-dialog]');
    const player = root.querySelector('[data-sa-real-player]');
    const closer = root.querySelector('[data-sa-real-close]');

    /* muted, looped, inline, and only while at least half of it shows */
    const play = (v) => {
      if (calm.matches || document.hidden) return;
      if (v.preload === 'none') v.preload = 'auto';
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };
    /* how much of each clip shows, as the observer last saw it: closing the
       player starts again only the clips that were playing by this rule */
    const seen = new Map();
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          const ratio = e.isIntersecting ? e.intersectionRatio : 0;
          seen.set(e.target, ratio);
          if (ratio >= 0.5) play(e.target);
          else e.target.pause();
        });
      }, { threshold: [0, 0.5, 1] });
      previews.forEach((v) => io.observe(v));
    }
    /* hidden, everything stops; back on the page, the clips that were half on
       screen play again (the observer does not fire, nothing moved) unless the
       full-screen player is open */
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { previews.forEach((v) => v.pause()); return; }
      if (dialog && (dialog.open || dialog.hasAttribute('open'))) return;
      previews.forEach((v) => { if ((seen.get(v) || 0) >= 0.5) play(v); });
    });

    /* arrows, for a mouse, when the row holds more clips than it shows */
    const rail = root.querySelector('.sa-real__rail');
    const nav = root.querySelector('[data-sa-real-nav]');
    if (rail && nav) {
      const steps = [...nav.querySelectorAll('[data-sa-real-step]')];
      const step = () => {
        const item = rail.querySelector('.sa-real__item');
        const gap = parseFloat(getComputedStyle(rail).columnGap) || 0;
        return item ? item.getBoundingClientRect().width + gap : rail.clientWidth * 0.8;
      };
      const state = () => {
        const max = rail.scrollWidth - rail.clientWidth - 1;
        nav.hidden = max <= 0;
        steps.forEach((b) => {
          b.disabled = b.dataset.saRealStep === '-1' ? rail.scrollLeft <= 0 : rail.scrollLeft >= max;
        });
      };
      steps.forEach((b) => b.addEventListener('click', () => {
        rail.scrollBy({ left: Number(b.dataset.saRealStep) * step(), behavior: calm.matches ? 'auto' : 'smooth' });
      }));
      rail.addEventListener('scroll', state, { passive: true });
      window.addEventListener('resize', state, { passive: true });
      state();
    }

    if (!dialog || !player) return;
    let opener = null;
    const open = (btn) => {
      opener = btn;
      const src = btn.getAttribute('data-src');
      if (!src) return;
      previews.forEach((v) => v.pause());
      player.src = src;
      const poster = btn.querySelector('video')?.getAttribute('poster');
      if (poster) player.poster = poster;
      player.muted = false;
      if (typeof dialog.showModal === 'function') dialog.showModal();
      else dialog.setAttribute('open', '');
      document.documentElement.classList.add('sa-real-open');
      const p = player.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
      closer?.focus();
    };
    const stop = () => {
      player.pause();
      player.removeAttribute('src');
      player.load();
      document.documentElement.classList.remove('sa-real-open');
      if (opener) opener.focus();
      previews.forEach((v) => {
        if ((seen.get(v) || 0) >= 0.5) play(v);
      });
    };
    const shut = () => {
      if (dialog.open && typeof dialog.close === 'function') dialog.close();
      else { dialog.removeAttribute('open'); stop(); }
    };
    root.querySelectorAll('[data-sa-real-open]').forEach((btn) => btn.addEventListener('click', () => open(btn)));
    closer?.addEventListener('click', shut);
    dialog.addEventListener('close', stop);
    player.addEventListener('ended', shut);
  };

  const all = () => document.querySelectorAll('[data-sa-real]').forEach(bind);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', all);
  else all();
  document.addEventListener('shopify:section:load', all);
})();
